import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';

const WarsPage = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [wars, setWars] = useState([]);
  const [myFaction, setMyFaction] = useState(null);
  const [territories, setTerritories] = useState([]);
  const [allFactions, setAllFactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeclareModal, setShowDeclareModal] = useState(false);
  const [declareForm, setDeclareForm] = useState({ defender_faction_id: '', territory_at_stake: '' });
  const [declareLoading, setDeclareLoading] = useState(false);
  const [declareError, setDeclareError] = useState(null);
  const [warResults, setWarResults] = useState({});
  const [warSnapshots, setWarSnapshots] = useState({});

  const fetchWarsData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (user?.id) {
        const { data: membership } = await supabase?.from('faction_members')?.select('faction_id, rank, factions(id, faction_name, tag, banner_color, total_vault_balance, faction_xp)')?.eq('user_id', user?.id)?.maybeSingle();
        setMyFaction(membership);
      }

      const { data: warsData, error: warsErr } = await supabase?.from('faction_wars')?.select('*, attacker:factions!faction_wars_attacker_faction_id_fkey(faction_name, tag, banner_color), defender:factions!faction_wars_defender_faction_id_fkey(faction_name, tag, banner_color)')?.order('created_at', { ascending: false })?.limit(50);

      if (warsErr) throw warsErr;
      setWars(warsData || []);

      if (warsData?.length > 0) {
        const warIds = warsData?.map(w => w?.id);
        const { data: resultsData } = await supabase?.from('war_results')?.select('*')?.in('war_id', warIds);
        const resultsMap = {};
        resultsData?.forEach(r => { resultsMap[r.war_id] = r; });
        setWarResults(resultsMap);

        const { data: snapshotsData } = await supabase?.from('war_snapshots')?.select('*')?.in('war_id', warIds);
        const snapshotsMap = {};
        snapshotsData?.forEach(s => {
          if (!snapshotsMap?.[s?.war_id]) snapshotsMap[s.war_id] = {};
          snapshotsMap[s.war_id][s.is_attacker ? 'attacker' : 'defender'] = s;
        });
        setWarSnapshots(snapshotsMap);
      }

      const { data: terrData } = await supabase?.from('territory_ownership')?.select('*')?.is('faction_id', null);
      setTerritories(terrData || []);

      const { data: factionsData } = await supabase?.from('factions')?.select('id, faction_name, tag')?.eq('is_active', true);
      setAllFactions(factionsData || []);
    } catch (err) {
      setError(err?.message || 'Failed to load wars data');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchWarsData(); }, [fetchWarsData]);

  useRealtimeSubscription(
    'faction-wars-live',
    { table: 'faction_wars', event: '*' },
    useCallback(() => { fetchWarsData(); }, [fetchWarsData]),
    { enabled: true }
  );

  const handleDeclareWar = async () => {
    setDeclareLoading(true); setDeclareError(null);
    try {
      const { data, error } = await supabase?.rpc('declare_war', {
        p_defender_faction_id: declareForm?.defender_faction_id,
        p_territory_at_stake: declareForm?.territory_at_stake || null,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);
      setShowDeclareModal(false);
      setDeclareForm({ defender_faction_id: '', territory_at_stake: '' });
      fetchWarsData();
    } catch (err) {
      setDeclareError(err?.message);
    } finally {
      setDeclareLoading(false);
    }
  };

  const formatTimeLeft = (endTime) => {
    const diff = new Date(endTime) - new Date();
    if (diff <= 0) return 'Ended';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${h}h ${m}m`;
  };

  const statusColor = (status) => {
    const colors = { DECLARED: 'bg-warning/10 text-warning border-warning/20', ACTIVE: 'bg-red-500/10 text-red-400 border-red-500/20', RESOLVED: 'bg-surface text-muted-foreground border-border', CANCELLED: 'bg-surface text-muted-foreground border-border' };
    return colors?.[status] || colors?.RESOLVED;
  };

  const filteredWars = wars?.filter(w => {
    if (activeTab === 'active') return ['DECLARED', 'ACTIVE']?.includes(w?.war_status);
    return ['RESOLVED', 'CANCELLED']?.includes(w?.war_status);
  });

  const myActiveWars = wars?.filter(w =>
    ['DECLARED', 'ACTIVE']?.includes(w?.war_status) &&
    (w?.attacker_faction_id === myFaction?.faction_id || w?.defender_faction_id === myFaction?.faction_id)
  );

  const myWarsWon = wars?.filter(w => {
    const result = warResults?.[w?.id];
    return result?.victor_faction_id === myFaction?.faction_id;
  })?.length;

  if (loading) {
    return (
      <div className="flex h-screen bg-background text-foreground overflow-hidden">
        <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
            <p className="text-muted-foreground">Loading wars data...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-h2 font-heading text-foreground flex items-center gap-3">
                <Icon name="Swords" size={28} color="#EF4444" />
                Territory Wars
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Faction battles for territory control</p>
            </div>
            {myFaction && (
              <button
                onClick={() => setShowDeclareModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded-md hover:bg-red-500/20 transition-colors text-sm font-semibold"
              >
                <Icon name="Swords" size={14} />
                Declare War
              </button>
            )}
          </div>

          {error && (
            <div className="card p-4 border-red-500/30 mb-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="card p-4 text-center">
              <Icon name="Swords" size={20} className="text-red-400 mx-auto mb-2" />
              <p className="text-2xl font-mono font-bold text-red-400">{myActiveWars?.length}</p>
              <p className="text-xs text-muted-foreground">Active Wars</p>
            </div>
            <div className="card p-4 text-center">
              <Icon name="Map" size={20} className="text-primary mx-auto mb-2" />
              <p className="text-2xl font-mono font-bold text-primary">{myFaction ? wars?.filter(w => w?.attacker_faction_id === myFaction?.faction_id || w?.defender_faction_id === myFaction?.faction_id)?.length : 0}</p>
              <p className="text-xs text-muted-foreground">Total Wars</p>
            </div>
            <div className="card p-4 text-center">
              <Icon name="Trophy" size={20} className="text-warning mx-auto mb-2" />
              <p className="text-2xl font-mono font-bold text-warning">{myWarsWon}</p>
              <p className="text-xs text-muted-foreground">Wars Won</p>
            </div>
          </div>

          <div className="flex gap-1 mb-4 bg-surface rounded-lg p-1 w-fit">
            {['active', 'history']?.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-all ${
                  activeTab === tab ? 'bg-primary text-zinc-950' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {filteredWars?.length === 0 ? (
              <div className="card p-8 text-center">
                <Icon name="Swords" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">{activeTab === 'active' ? 'No active wars' : 'No war history'}</p>
              </div>
            ) : (
              filteredWars?.map(war => {
                const result = warResults?.[war?.id];
                const snapshots = warSnapshots?.[war?.id];
                const attackerScore = snapshots ? (
                  (Number(snapshots?.attacker?.total_net_worth || 0) * 0.4) +
                  (Number(snapshots?.attacker?.total_faction_xp || 0) * 0.3)
                ) : 0;
                const defenderScore = snapshots ? (
                  (Number(snapshots?.defender?.total_net_worth || 0) * 0.4) +
                  (Number(snapshots?.defender?.total_faction_xp || 0) * 0.3)
                ) : 0;
                const totalScore = attackerScore + defenderScore;

                return (
                  <div key={war?.id} className={`card p-5 border ${
                    war?.war_status === 'ACTIVE' ? 'border-red-500/30' :
                    war?.war_status === 'DECLARED' ? 'border-warning/30' : 'border-border'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold uppercase border ${statusColor(war?.war_status)}`}>
                          {war?.war_status}
                        </span>
                        {war?.territory_at_stake && (
                          <span className="text-xs text-muted-foreground">for {war?.territory_at_stake?.replace(/_/g, ' ')}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {['DECLARED', 'ACTIVE']?.includes(war?.war_status) && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Icon name="Clock" size={12} />
                            {formatTimeLeft(war?.end_time)}
                          </span>
                        )}
                        {result && (
                          <span className="text-xs text-success flex items-center gap-1">
                            <Icon name="Trophy" size={12} />
                            {result?.victor_faction_id === war?.attacker_faction_id ? war?.attacker?.faction_name : war?.defender?.faction_name} won
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 text-center">
                        <div
                          className="inline-block px-2 py-0.5 rounded text-xs font-mono font-bold mb-1"
                          style={{ background: (war?.attacker?.banner_color || '#00D9FF') + '20', color: war?.attacker?.banner_color || '#00D9FF' }}
                        >
                          [{war?.attacker?.tag}]
                        </div>
                        <p className="text-sm font-semibold text-foreground">{war?.attacker?.faction_name}</p>
                        <p className="text-xl font-mono font-bold text-primary mt-1">
                          {result ? Number(result?.war_score_attacker || 0)?.toFixed(0) : attackerScore?.toFixed(0)}
                        </p>
                        <p className="text-xs text-muted-foreground">Attacker</p>
                      </div>

                      <div className="text-center">
                        <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                          <Icon name="Swords" size={18} color="#EF4444" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">VS</p>
                      </div>

                      <div className="flex-1 text-center">
                        <div
                          className="inline-block px-2 py-0.5 rounded text-xs font-mono font-bold mb-1"
                          style={{ background: (war?.defender?.banner_color || '#FF6B6B') + '20', color: war?.defender?.banner_color || '#FF6B6B' }}
                        >
                          [{war?.defender?.tag}]
                        </div>
                        <p className="text-sm font-semibold text-foreground">{war?.defender?.faction_name}</p>
                        <p className="text-xl font-mono font-bold text-red-400 mt-1">
                          {result ? Number(result?.war_score_defender || 0)?.toFixed(0) : defenderScore?.toFixed(0)}
                        </p>
                        <p className="text-xs text-muted-foreground">Defender</p>
                      </div>
                    </div>
                    {war?.war_status === 'ACTIVE' && totalScore > 0 && (
                      <div className="mt-4">
                        <div className="w-full h-2 bg-surface rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-primary to-red-400 transition-all"
                            style={{ width: `${(attackerScore / totalScore) * 100}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>War Cost: ${Number(war?.war_cost || 0)?.toLocaleString()}</span>
                          <span>Ends: {new Date(war?.end_time)?.toLocaleString()}</span>
                        </div>
                      </div>
                    )}
                    {snapshots && (
                      <div className="mt-3 pt-3 border-t border-border/30 grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="text-muted-foreground mb-1">Attacker Snapshot</p>
                          <div className="space-y-0.5">
                            <p>Members: <span className="text-foreground font-mono">{snapshots?.attacker?.total_members}</span></p>
                            <p>Avg Level: <span className="text-foreground font-mono">{Number(snapshots?.attacker?.average_level || 0)?.toFixed(1)}</span></p>
                            <p>Net Worth: <span className="text-foreground font-mono">${Number(snapshots?.attacker?.total_net_worth || 0)?.toLocaleString()}</span></p>
                          </div>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Defender Snapshot</p>
                          <div className="space-y-0.5">
                            <p>Members: <span className="text-foreground font-mono">{snapshots?.defender?.total_members}</span></p>
                            <p>Avg Level: <span className="text-foreground font-mono">{Number(snapshots?.defender?.average_level || 0)?.toFixed(1)}</span></p>
                            <p>Net Worth: <span className="text-foreground font-mono">${Number(snapshots?.defender?.total_net_worth || 0)?.toLocaleString()}</span></p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>
      {showDeclareModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
                <Icon name="Swords" size={18} color="#EF4444" />
                Declare War
              </h2>
              <button onClick={() => setShowDeclareModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">War cost: <span className="text-warning font-mono">$25,000</span> from faction vault. 7-day cooldown between same factions.</p>
            {declareError && <p className="text-red-400 text-sm mb-3">{declareError}</p>}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Target Faction</label>
                <select
                  value={declareForm?.defender_faction_id}
                  onChange={e => setDeclareForm(p => ({ ...p, defender_faction_id: e?.target?.value }))}
                  className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-foreground"
                >
                  <option value="">Select faction...</option>
                  {allFactions?.filter(f => f?.id !== myFaction?.faction_id)?.map(f => (
                    <option key={f?.id} value={f?.id}>[{f?.tag}] {f?.faction_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Territory at Stake (optional)</label>
                <select
                  value={declareForm?.territory_at_stake}
                  onChange={e => setDeclareForm(p => ({ ...p, territory_at_stake: e?.target?.value }))}
                  className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-foreground"
                >
                  <option value="">No territory</option>
                  {territories?.map(t => (
                    <option key={t?.territory_id} value={t?.territory_id}>{t?.territory_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowDeclareModal(false)}
                className="flex-1 py-2 bg-surface border border-border rounded-md text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                onClick={handleDeclareWar}
                disabled={declareLoading || !declareForm?.defender_faction_id}
                className="flex-1 py-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded-md text-sm font-semibold hover:bg-red-500/20 disabled:opacity-50"
              >
                {declareLoading ? 'Declaring...' : 'Declare War'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarsPage;
